import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileSearch, Sparkles, Users, Calendar, AlertTriangle, Trash2, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { it as itLocale, enUS } from "date-fns/locale";

export const Route = createFileRoute("/_authenticated/app/analyses")({
  component: AnalysesPage,
});

type Row = {
  id: string;
  source_name: string;
  created_at: string;
  result: any;
};

function AnalysesPage() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.resolvedLanguage?.startsWith("en");
  const dateLocale = isEn ? "en-US" : "it-IT";
  const fnsLocale = isEn ? enUS : itLocale;
  const [rows, setRows] = useState<Row[] | null>(null);
  const [active, setActive] = useState<Row | null>(null);

  const load = async () => {
    const { data, error } = await supabase
      .from("document_analyses")
      .select("id, source_name, created_at, result")
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows((data as Row[]) ?? []);
  };

  useEffect(() => { load(); }, []);

  const remove = async (id: string) => {
    if (!confirm(t("pages.analyses.deleteConfirm"))) return;
    const { error } = await supabase.from("document_analyses").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success(t("pages.analyses.deleted"));
    setActive(null);
    load();
  };

  if (active) {
    const a = active.result ?? {};
    return (
      <>
        <PageHeader
          title={active.source_name}
          description={t("pages.analyses.analysisOf", { date: new Date(active.created_at).toLocaleString(dateLocale) })}
          actions={
            <>
              <Button variant="outline" size="sm" onClick={() => setActive(null)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> {t("pages.analyses.backToHistory")}
              </Button>
              <Button variant="outline" size="sm" onClick={() => remove(active.id)} className="text-destructive">
                <Trash2 className="mr-2 h-4 w-4" /> {t("pages.analyses.delete")}
              </Button>
            </>
          }
        />
        <PageBody>
          <Card className="overflow-hidden">
            <Tabs defaultValue="summary" className="flex flex-col">
              <TabsList className="m-3 grid grid-cols-5">
                <TabsTrigger value="summary"><Sparkles className="mr-1 h-3 w-3" />{t("pages.analyses.tabSummary")}</TabsTrigger>
                <TabsTrigger value="parties"><Users className="mr-1 h-3 w-3" />{t("pages.analyses.tabParties")}</TabsTrigger>
                <TabsTrigger value="dates"><Calendar className="mr-1 h-3 w-3" />{t("pages.analyses.tabDates")}</TabsTrigger>
                <TabsTrigger value="clauses">{t("pages.analyses.tabClauses")}</TabsTrigger>
                <TabsTrigger value="risks"><AlertTriangle className="mr-1 h-3 w-3" />{t("pages.analyses.tabRisks")}</TabsTrigger>
              </TabsList>
              <div className="px-5 pb-6">
                <TabsContent value="summary" className="space-y-4">
                  {a.documentType && <Badge variant="secondary">{a.documentType}</Badge>}
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">{a.summary}</p>
                  {a.suggestions?.length > 0 && (
                    <div>
                      <h4 className="mb-2 font-display font-semibold">{t("pages.analyses.suggestedActions")}</h4>
                      <ul className="space-y-1.5 text-sm">
                        {a.suggestions.map((s: string, i: number) => (
                          <li key={i} className="flex gap-2"><span className="text-accent">→</span>{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="parties" className="space-y-2">
                  {(a.parties ?? []).map((p: any, i: number) => (
                    <div key={i} className="rounded-md border p-3">
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.role}</div>
                    </div>
                  ))}
                </TabsContent>
                <TabsContent value="dates" className="space-y-2">
                  {(a.keyDates ?? []).map((d: any, i: number) => (
                    <div key={i} className="flex gap-3 rounded-md border p-3">
                      <div className="font-mono text-sm font-medium text-accent">{d.date}</div>
                      <div className="text-sm">{d.description}</div>
                    </div>
                  ))}
                </TabsContent>
                <TabsContent value="clauses" className="space-y-3">
                  {(a.clauses ?? []).map((c: any, i: number) => (
                    <div key={i} className="rounded-md border p-4">
                      <div className="font-display font-semibold">{c.title}</div>
                      <blockquote className="mt-2 border-l-2 border-accent pl-3 text-sm italic text-muted-foreground">
                        {c.quote}
                      </blockquote>
                      <p className="mt-2 text-sm">{c.comment}</p>
                    </div>
                  ))}
                </TabsContent>
                <TabsContent value="risks" className="space-y-3">
                  {(a.risks ?? []).map((r: any, i: number) => (
                    <div key={i} className="rounded-md border p-4">
                      <div className="flex items-center gap-2">
                        <Badge variant={r.severity === "high" ? "destructive" : r.severity === "medium" ? "default" : "secondary"}>
                          {r.severity === "high" ? t("pages.analyses.sevHigh") : r.severity === "medium" ? t("pages.analyses.sevMedium") : t("pages.analyses.sevLow")}
                        </Badge>
                        <span className="font-medium">{r.title}</span>
                      </div>
                      <p className="mt-2 text-sm">{r.description}</p>
                      {r.mitigation && (
                        <p className="mt-2 rounded bg-muted p-2 text-xs">
                          <strong>{t("pages.analyses.mitigation")}:</strong> {r.mitigation}
                        </p>
                      )}
                    </div>
                  ))}
                </TabsContent>
              </div>
            </Tabs>
          </Card>
        </PageBody>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("pages.analyses.title")}
        description={t("pages.analyses.description")}
        actions={
          <Button asChild size="sm">
            <Link to="/app/analysis">{t("pages.analyses.newAnalysis")}</Link>
          </Button>
        }
      />
      <PageBody>
        {rows === null ? (
          <Card className="flex items-center justify-center p-12"><Loader2 className="h-5 w-5 animate-spin" /></Card>
        ) : rows.length === 0 ? (
          <Card className="flex flex-col items-center justify-center p-12 text-center">
            <FileSearch className="mb-3 h-10 w-10 text-muted-foreground" />
            <h3 className="font-display text-lg font-semibold">{t("pages.analyses.noneTitle")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("pages.analyses.noneDesc")}</p>
            <Button asChild className="mt-4"><Link to="/app/analysis">{t("pages.analyses.analyzeDocument")}</Link></Button>
          </Card>
        ) : (
          <div className="grid gap-3">
            {rows.map((r) => {
              const a = r.result ?? {};
              const risks = (a.risks ?? []) as any[];
              const high = risks.filter((x) => x.severity === "high").length;
              return (
                <Card
                  key={r.id}
                  onClick={() => setActive(r)}
                  className="group cursor-pointer p-5 transition-all hover:border-foreground/20 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <FileSearch className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <h3 className="truncate font-medium">{r.source_name}</h3>
                        {a.documentType && <Badge variant="secondary" className="text-[10px]">{a.documentType}</Badge>}
                      </div>
                      <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{a.summary}</p>
                      <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
                        <span>📅 {formatDistanceToNow(new Date(r.created_at), { addSuffix: true, locale: fnsLocale })}</span>
                        {a.parties?.length > 0 && <span>👥 {t("pages.analyses.partiesCount", { n: a.parties.length })}</span>}
                        {a.clauses?.length > 0 && <span>📋 {t("pages.analyses.clausesCount", { n: a.clauses.length })}</span>}
                        {risks.length > 0 && (
                          <span className={high > 0 ? "text-destructive font-medium" : ""}>
                            ⚠️ {t("pages.analyses.risksCount", { n: risks.length })}{high > 0 ? ` ${t("pages.analyses.risksHigh", { n: high })}` : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </PageBody>
    </>
  );
}
